import React, { useRef, useEffect, useState, useCallback } from 'react';
import WaveSurfer from 'wavesurfer.js';

const MiniWaveform = ({ audioSrc, currentTime, duration, onSeek }) => {
  const containerRef = useRef(null);
  const waveformRef = useRef(null);
  const wavesurferRef = useRef(null);
  const [isReady, setIsReady] = useState(false);
  const isDraggingRef = useRef(false);

  // Initialize WaveSurfer
  useEffect(() => {
    if (!waveformRef.current || !audioSrc) return;

    let wavesurfer = null;
    let isCancelled = false;

    const initWaveSurfer = async () => {
      try {
        // Create minimal WaveSurfer instance
        wavesurfer = WaveSurfer.create({
          container: waveformRef.current,
          waveColor: '#cbd5e1',
          progressColor: '#f73140',
          cursorColor: '#f73140',
          cursorWidth: 2,
          barWidth: 3,
          barGap: 2,
          barRadius: 3,
          height: 48,
          normalize: true,
          interact: true,
          hideScrollbar: true,
          fillParent: true,
        });

        if (isCancelled) {
          wavesurfer.destroy();
          return;
        }

        // When ready
        wavesurfer.on('ready', () => {
          if (!isCancelled) {
            setIsReady(true);
          }
        });

        // Handle click to seek
        wavesurfer.on('interaction', (time) => {
          if (!isCancelled && onSeek) {
            onSeek(time);
          }
        });

        // Handle errors silently
        wavesurfer.on('error', () => {});

        wavesurferRef.current = wavesurfer;

        // Load audio
        if (!isCancelled) {
          await wavesurfer.load(audioSrc);
        }
      } catch (error) {
        // Silently ignore errors
      }
    };

    initWaveSurfer();

    return () => {
      isCancelled = true;
      setIsReady(false);
      wavesurferRef.current = null;
      
      setTimeout(() => {
        try {
          if (wavesurfer) {
            wavesurfer.destroy();
          }
        } catch (e) {
          // Silently ignore
        }
      }, 0);
    };
  }, [audioSrc]);

  // Sync playback position
  useEffect(() => {
    if (wavesurferRef.current && isReady && duration > 0) {
      const ws = wavesurferRef.current;
      const wsTime = ws.getCurrentTime();
      const diff = Math.abs(wsTime - currentTime);
      
      // Only update if significantly different
      if (diff > 0.05) {
        ws.setTime(currentTime);
      }
    }
  }, [currentTime, isReady, duration]);

  const seekFromPointerEvent = useCallback((event) => {
    if (!onSeek || !containerRef.current || !duration) return;
    const rect = containerRef.current.getBoundingClientRect();
    if (!rect.width) return;
    const percent = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
    onSeek(percent * duration);
  }, [onSeek, duration]);

  const handlePointerDown = useCallback((event) => {
    if (!containerRef.current) return;
    event.preventDefault();
    isDraggingRef.current = true;
    containerRef.current.setPointerCapture?.(event.pointerId);
    seekFromPointerEvent(event);
  }, [seekFromPointerEvent]);

  const handlePointerMove = useCallback((event) => {
    if (!isDraggingRef.current) return;
    event.preventDefault();
    seekFromPointerEvent(event);
  }, [seekFromPointerEvent]);

  const handlePointerUp = useCallback((event) => {
    if (!containerRef.current) return;
    if (isDraggingRef.current) {
      isDraggingRef.current = false;
      containerRef.current.releasePointerCapture?.(event.pointerId);
    }
  }, []);

  if (!audioSrc) return null;

  return (
    <div
      className="w-full h-12 relative rounded-xl overflow-hidden"
      ref={containerRef}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      style={{ touchAction: 'none' }}
    >
      <div 
        className="w-full h-full" 
        ref={waveformRef}
      >
        {!isReady && (
          <div className="absolute inset-0 flex items-center justify-center rounded-xl bg-slate-50">
            <div className="flex items-center gap-1">
              {[...Array(20)].map((_, i) => (
                <div 
                  key={i}
                  className="w-1 rounded-full animate-pulse bg-slate-200"
                  style={{ 
                    height: `${Math.random() * 60 + 20}%`,
                    animationDelay: `${i * 50}ms`
                  }}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MiniWaveform;
